import { useCallback, useEffect, useRef, useState } from "react";
import { isSamePath } from "./samePath";

const GUARD_ENTRY_MARK = "navigationGuardEntry";

function onGuardEntry(): boolean {
  return window.history.state?.[GUARD_ENTRY_MARK] === true;
}

function pushGuardEntry() {
  window.history.pushState({ [GUARD_ENTRY_MARK]: true }, "", window.location.href);
}

type NavigationGuardState = {
  blocked: boolean;
  pendingUrl: string | null;
};

type NavigationGuardOptions = {
  allowPath?: string;
  interceptBack?: boolean;
};

export function useNavigationGuard(isDirty: boolean, options: NavigationGuardOptions = {}) {
  const { allowPath, interceptBack = true } = options;
  const isDirtyRef = useRef(isDirty);
  isDirtyRef.current = isDirty;

  const [guard, setGuard] = useState<NavigationGuardState>({
    blocked: false,
    pendingUrl: null,
  });

  const skipGuardRef = useRef(false);

  useEffect(() => {
    if (!isDirty) return;

    function handleBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty || !interceptBack) return;

    function handlePopState() {
      if (!isDirtyRef.current || skipGuardRef.current) return;
      pushGuardEntry();
      setGuard({ blocked: true, pendingUrl: "back" });
    }

    if (!onGuardEntry()) pushGuardEntry();
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty, interceptBack]);

  useEffect(() => {
    if (!isDirty) return;

    const originalPushState = window.history.pushState.bind(window.history);

    window.history.pushState = (data: unknown, unused: string, url?: string | URL | null) => {
      if (isDirtyRef.current && url && !skipGuardRef.current) {
        const toAllowedPath = allowPath !== undefined && isSamePath(url, allowPath);
        if (!toAllowedPath && !isSamePath(url, window.location.pathname)) {
          setGuard({ blocked: true, pendingUrl: typeof url === "string" ? url : url.pathname });
          return;
        }
      }
      originalPushState(data, unused, url);
    };

    return () => {
      window.history.pushState = originalPushState;
    };
  }, [isDirty, allowPath]);

  useEffect(() => {
    const originalReplaceState = window.history.replaceState.bind(window.history);

    window.history.replaceState = (data: unknown, unused: string, url?: string | URL | null) => {
      const keepsMark = onGuardEntry() && (data === null || typeof data === "object");
      originalReplaceState(keepsMark ? { ...data, [GUARD_ENTRY_MARK]: true } : data, unused, url);
    };

    return () => {
      window.history.replaceState = originalReplaceState;
    };
  }, []);

  const cancel = useCallback(() => {
    setGuard({ blocked: false, pendingUrl: null });
  }, []);

  const proceed = useCallback(() => {
    const url = guard.pendingUrl;
    setGuard({ blocked: false, pendingUrl: null });
    skipGuardRef.current = true;

    if (url === "back") {
      window.history.go(-2);
    } else if (url) {
      window.location.assign(url);
    }

    setTimeout(() => {
      skipGuardRef.current = false;
    }, 100);
  }, [guard.pendingUrl]);

  return { blocked: guard.blocked, cancel, proceed };
}
